import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, RefreshCw } from 'lucide-react';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import { apiService } from '../services/api';

function formatMaintenanceDateTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoString;
  }
}

const MaintenancePage: React.FC = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>(
    'We are currently performing scheduled maintenance. Please check back shortly.'
  );
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const active = await apiService.getMaintenanceStatus();
        if (active?.message) setMessage(active.message);
        if (active?.from) setFrom(active.from);
        if (active?.to) setTo(active.to);
      } catch {
        // Keep default message on error
      }
    };
    load();
  }, []);

  const handleCheckAgain = async () => {
    setChecking(true);
    try {
      const active = await apiService.getMaintenanceStatus();
      if (!active) {
        navigate('/', { replace: true });
        return;
      }
      if (active.message) setMessage(active.message);
      if (active.from) setFrom(active.from);
      if (active.to) setTo(active.to);
    } catch {
      // Stay on page
    } finally {
      setChecking(false);
    }
  };

  const hasWindow = from || to;
  const windowText =
    from && to
      ? `${formatMaintenanceDateTime(from)} – ${formatMaintenanceDateTime(to)}`
      : from
        ? formatMaintenanceDateTime(from)
        : to
          ? formatMaintenanceDateTime(to)
          : null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col no-animations">
      <CustomerNavbar
        showDashboardLink={false}
        showLogoutButton={false}
        showAuthButtons={false}
      />
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200/80 overflow-hidden">
          {/* Header strip */}
          <div className="bg-slate-800 px-6 py-5 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-400/20 text-amber-400 mb-4">
              <Wrench className="w-7 h-7" strokeWidth={2} aria-hidden />
            </div>
            <h1 className="text-xl font-semibold text-white tracking-tight">
              Scheduled Maintenance
            </h1>
            <p className="mt-1 text-sm text-slate-300">
              We’ll be back shortly. Thank you for your patience.
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-6 sm:px-8 sm:py-7">
            <p className="text-slate-600 text-[15px] leading-relaxed whitespace-pre-wrap">
              {message}
            </p>

            {hasWindow && windowText && (
              <div className="mt-5 flex gap-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3.5">
                <div className="flex-shrink-0 mt-0.5 text-slate-400">
                  <Clock className="w-5 h-5" strokeWidth={2} aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Maintenance Window
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-700">
                    {windowText}
                  </p>
                </div>
              </div>
            )}

            <div className="mt-6 pt-5 border-t border-slate-100">
              <button
                type="button"
                onClick={handleCheckAgain}
                disabled={checking}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-slate-800 rounded-xl hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw
                  className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`}
                  strokeWidth={2}
                  aria-hidden
                />
                {checking ? 'Checking…' : 'Check again'}
              </button>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          If you believe you’ve reached this page in error, try again in a few minutes.
        </p>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default MaintenancePage;
